import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

class AppInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private isInitialized = false;

  /**
   * Initialize Application Insights (call once at app startup)
   */
  initialize(): ReactPlugin {
    if (this.isInitialized) {
      return this.reactPlugin!;
    }

    try {
      const connectionString = import.meta.env.VITE_APPLICATION_INSIGHTS_INSTRUMENTATION_KEY;

      // Validate connection string
      if (!connectionString) {
        // Can't use logger here as it would create circular dependency
        // Logger depends on appInsights, so we use console in this one place
        console.error("Application Insights connection string is missing");
        // Return a dummy plugin to prevent crashes
        this.reactPlugin = new ReactPlugin();
        return this.reactPlugin;
      }

      // Create React plugin
      this.reactPlugin = new ReactPlugin();

      // Get sampling percentage based on environment
      const samplingPercentage = this.getSamplingPercentage();

      // Check if telemetry is disabled
      const isTelemetryDisabled =
        import.meta.env.DEV && import.meta.env.VITE_DISABLE_TELEMETRY === "true";

      // Create Application Insights instance
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,

          // Automatic tracking
          enableAutoRouteTracking: true,
          autoTrackPageVisitTime: true,

          // AJAX/Fetch tracking
          disableAjaxTracking: false,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,

          // CORS correlation
          enableCorsCorrelation: true,

          // Sampling - reduce data volume in production
          samplingPercentage,

          // Extensions
          extensions: [this.reactPlugin],
          extensionConfig: {},

          // Disable telemetry in development (optional)
          disableTelemetry: isTelemetryDisabled,
        },
      });

      // Load and initialize
      this.appInsights.loadAppInsights();
      this.isInitialized = true;

      return this.reactPlugin;
    } catch (error) {
      // Can't use logger here as it would create circular dependency
      // Logger depends on appInsights, so we use console in this one place
      console.error("Failed to initialize Application Insights:", error);
      // Create a fallback plugin to prevent app crashes
      if (!this.reactPlugin) {
        this.reactPlugin = new ReactPlugin();
      }
      return this.reactPlugin;
    }
  }

  /**
   * Get sampling percentage based on environment
   */
  private getSamplingPercentage(): number {
    if (import.meta.env.DEV) return 100; // 100% in development
    if (import.meta.env.VITE_APP_ENV === "staging") return 75; // 75% in staging
    if (import.meta.env.VITE_APP_ENV === "production") return 50; // 50% in production
    return 100; // Default to 100%
  }

  /**
   * Get the React plugin instance
   */
  getReactPlugin(): ReactPlugin | null {
    return this.reactPlugin;
  }

  /**
   * Get the Application Insights instance
   */
  getInstance(): ApplicationInsights | null {
    return this.appInsights;
  }

  /**
   * Track custom event
   */
  trackEvent(name: string, properties?: { [key: string]: any }): void {
    if (!this.appInsights) return;
    this.appInsights.trackEvent({ name, properties });
  }

  /**
   * Track exception
   */
  trackException(error: Error, properties?: { [key: string]: any }): void {
    if (!this.appInsights) return;
    this.appInsights.trackException({ exception: error, properties });
  }

  /**
   * Track API call performance
   */
  trackApiCall(
    apiName: string,
    duration: number,
    success: boolean,
    statusCode?: number,
    properties?: { [key: string]: any },
  ): void {
    if (!this.appInsights) return;

    this.appInsights.trackEvent({
      name: "ApiCall",
      properties: {
        apiName,
        duration,
        success,
        statusCode,
        ...properties,
      },
    });

    // Also track as metric for better analysis
    this.appInsights.trackMetric({
      name: `ApiDuration_${apiName}`,
      average: duration,
    });
  }

  /**
   * Track page load performance
   */
  trackPageLoadTime(pageName: string): void {
    if (!this.appInsights) return;

    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      const pageLoadTime = timing.loadEventEnd - timing.navigationStart;
      const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;

      this.appInsights.trackMetric({
        name: `PageLoadTime_${pageName}`,
        average: pageLoadTime,
      });

      this.appInsights.trackEvent({
        name: "PageLoad",
        properties: {
          pageName,
          pageLoadTime,
          domReadyTime,
        },
      });
    }
  }

  /**
   * Start tracking a timed operation
   */
  startTrackEvent(name: string): void {
    if (!this.appInsights) return;
    this.appInsights.startTrackEvent(name);
  }

  /**
   * Stop tracking a timed operation
   */
  stopTrackEvent(name: string, properties?: { [key: string]: any }): void {
    if (!this.appInsights) return;
    this.appInsights.stopTrackEvent(name, properties);
  }
}

// Export singleton instance
export const appInsightsService = new AppInsightsService();
